import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListAttachmentsResult } from '@whinerp/client';
import { Button, EmptyState, InlineAction, Num, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { formatCount } from '@whinerp/contracts/format';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';

/**
 * Files on a record (F-032).
 *
 * "Support drag-and-drop onto the whole record page, not only onto a small
 * drop target." So the listener is on the document while this panel is
 * mounted, and the panel highlights when something is being dragged anywhere -
 * a five-pixel target somebody has to hit is the reason people give up and use
 * email instead.
 *
 * The other thing on screen that is not a file name: whether it was scanned.
 * This deployment has no scanner, and a panel that stayed quiet about that
 * would let somebody open an attachment believing it had been checked.
 */
export function FilesPanel({
  objectKey,
  recordId,
}: {
  objectKey: string;
  recordId: string;
}): JSX.Element {
  const t = useT();
  const format = useFormat();
  const errorMessage = useErrorMessage();
  const client = useQueryClient();
  const [dragging, setDragging] = useState(false);
  const [tooBig, setTooBig] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const files = useQuery<ListAttachmentsResult, ApiError>({
    queryKey: ['files', objectKey, recordId],
    queryFn: () => api.listAttachments({ path: { objectKey, recordId } }),
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const bytes = new Uint8Array(await file.arrayBuffer());
      let binary = '';
      for (const byte of bytes) binary += String.fromCharCode(byte);
      return api.uploadAttachment({
        path: { objectKey, recordId },
        body: {
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          contentBase64: btoa(binary),
        },
      });
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['files', objectKey, recordId] });
    },
  });

  const remove = useMutation({
    mutationFn: (attachmentId: string) => api.deleteAttachment({ path: { attachmentId } }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['files', objectKey, recordId] });
    },
  });

  const maxBytes = files.data?.maxBytes ?? 0;
  const maxLabel = files.data?.maxLabel ?? '';

  const accept = (file: File): void => {
    /*
     * Checked here as well as on the server.
     *
     * Not instead of: the server is where the rule lives. But a browser that
     * spends thirty seconds base64-ing a file the server will refuse has
     * wasted somebody's time to tell them something it already knew.
     */
    if (maxBytes > 0 && file.size > maxBytes) {
      setTooBig(
        t('files.tooBig', {
          values: { size: `${formatCount(Math.round(file.size / 1024), format)} KB`, max: maxLabel },
        }),
      );
      return;
    }
    setTooBig(null);
    upload.mutate(file);
  };

  /*
   * The whole page, not a small target.
   *
   * Listeners on the document while this panel is mounted. `dragover` must
   * preventDefault or the browser navigates to the file instead, which is the
   * most confusing possible outcome of dropping one.
   */
  useEffect(() => {
    const over = (event: DragEvent): void => {
      event.preventDefault();
      setDragging(true);
    };
    const leave = (event: DragEvent): void => {
      if (event.relatedTarget === null) setDragging(false);
    };
    const drop = (event: DragEvent): void => {
      event.preventDefault();
      setDragging(false);
      const file = event.dataTransfer?.files?.[0];
      if (file) accept(file);
    };
    document.addEventListener('dragover', over);
    document.addEventListener('dragleave', leave);
    document.addEventListener('drop', drop);
    return () => {
      document.removeEventListener('dragover', over);
      document.removeEventListener('dragleave', leave);
      document.removeEventListener('drop', drop);
    };
  });

  if (files.isPending) return <Skeleton className="h-block-sm w-full" />;
  if (files.isError) return <></>;

  return (
    <Panel data-region="files" data-files-dragging={dragging}>
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('files.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">
          {t('files.drop')} · <Num>{t('files.max', { values: { size: maxLabel } })}</Num>
        </p>
        <div>
          <Button data-file-choose intent="secondary" onClick={() => input.current?.click()}>
            {t('files.choose')}
          </Button>
          {/*
            A mechanism, not a control - but a named one.

            The Button is what a person operates; this exists so clicking it
            opens the picker. `tabIndex={-1}` keeps it out of the tab order so
            it is not a second stop for the same action, and it still carries a
            name: `aria-hidden` on something that can be focused at all is
            itself the violation, and an unnamed input announces as "button"
            with nothing after it. The a11y check caught exactly that.
          */}
          <input
            ref={input}
            data-file-input
            type="file"
            className="sr-only"
            tabIndex={-1}
            aria-label={t('files.choose')}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) accept(file);
            }}
          />
        </div>
        {tooBig ? (
          <p role="alert" data-file-too-big className="text-meta text-danger">
            {tooBig}
          </p>
        ) : null}
        {upload.isError ? (
          <p role="alert" data-file-error className="text-meta text-danger">
            {errorMessage(upload.error)}
          </p>
        ) : null}

        {files.data.files.length === 0 ? (
          <EmptyState
            data-file-empty
            illustration="Paperclip"
            title={t('files.title')}
            body={t('files.empty')}
            primary={{ label: t('files.choose'), icon: 'Upload', onClick: () => input.current?.click() }}
          />
        ) : (
          <ul className="flex flex-col gap-1" data-file-list>
            {files.data.files.map((file) => (
              <li
                key={file.attachmentId}
                data-file={file.attachmentId}
                className="flex flex-wrap items-center gap-2 rounded-chip bg-sunken px-3 py-2"
              >
                <span className="text-body text-ink">{file.filename}</span>
                {/* Said, because "never overwrite" is only useful if somebody
                    can see that a file has been replaced. */}
                {file.version > 1 ? (
                  <Num data-file-version className="text-meta text-ink-meta">
                    {t('files.version', { values: { n: formatCount(file.version, format) } })}
                  </Num>
                ) : null}
                {/*
                  Whether it was checked.

                  A panel that stayed quiet about an unscanned file would let
                  somebody open it believing it had been checked - which is
                  exactly what the 'skipped' status exists to prevent.
                */}
                {file.scanStatus === 'skipped' ? (
                  <span data-file-unscanned title={file.scanDetail ?? ''} className="text-meta text-warning-text">
                    {t('files.notScanned')}
                  </span>
                ) : null}
                {file.scanStatus === 'infected' ? (
                  <span data-file-infected className="text-meta text-danger">
                    {t('files.infected')}
                  </span>
                ) : null}
                <InlineAction
                  data-file-remove={file.attachmentId}
                  className="ms-auto"
                  onClick={() => remove.mutate(file.attachmentId)}
                >
                  {t('files.remove')}
                </InlineAction>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  );
}
